import { gql, useMutation } from '@apollo/client'
import { useStripe } from '@stripe/react-stripe-js'
import { keys, isEmpty, reduce } from 'lodash'
import { useEffect, useRef, useState } from 'react'
import { Toast } from 'components/Toast'
import { multiply } from 'utils/money'

export const getPaymentCallbacks = ({
  ref,
  submit,
  submitFormatter,
  selectedCurrency,
  additionalData = {},
}: {
  ref: React.MutableRefObject<any>
  submit: (payload: any) => Promise<any>
  submitFormatter: (payload: any) => any
  selectedCurrency?: string
  additionalData?: any
}) => ({
  isAbleToPay: async () => {
    const validated = await ref?.current?.validateForm()
    if (ref?.current && !isEmpty(validated)) {
      // we need to set touch for inputs, so validation errors could show up
      ref.current.setTouched(
        reduce(
          keys(validated),
          (acc, field) => ({ ...acc, [field]: true }),
          {}
        ),
        true
      )
      return false
    }
    return true
  },
  onPaymentSuccess: async (paymentMethodPayload) =>
    Boolean(
      await submit(
        submitFormatter({
          ...ref?.current?.values,
          ...paymentMethodPayload,
          ...(selectedCurrency && { selectedCurrency }),
          ...additionalData,
        })
      )
    ),
})

const SETUP_PAYMENT_MUTATION = gql`
  mutation setupClientPaymentMethod {
    setupClientPaymentMethod
  }
`

export const usePaymentRequest = ({
  amount,
  currency,
  isAbleToPay,
  onPaymentSuccess,
}) => {
  const stripe = useStripe()
  const [executeSetupPaymentMutation, { loading: isSetupPaymentLoading }] =
    useMutation(SETUP_PAYMENT_MUTATION)
  const paymentRequestRef = useRef(null)
  const [isPaymentLoading, setIsPaymentLoading] = useState(false)
  const [paymentOptions, setPaymentOptions] = useState([])

  const paymentRequest = paymentRequestRef?.current

  const executePayment = async () => {
    if (
      paymentRequest &&
      !paymentRequest.isShowing() &&
      (await isAbleToPay())
    ) {
      paymentRequest.show()
    }
  }

  /**
   * Handle paymentRequest creation
   * @see https://stripe.com/docs/js/payment_request/create
   * */
  useEffect(() => {
    if (stripe && currency && amount > 0 && paymentRequest === null) {
      try {
        setIsPaymentLoading(true)
        const request = stripe.paymentRequest({
          country: 'US', // stripe account country
          currency: currency.toLowerCase(),
          total: {
            amount: multiply(amount, 100),
            label: 'Total',
          },
          requestPayerName: true,
          requestPayerEmail: true,
          disableWallets: ['googlePay', 'browserCard'],
        })
        // Check the availability of the Payment Request API.
        request
          .canMakePayment()
          .then((canMakePaymentResult) => {
            paymentRequestRef.current = undefined
            if (canMakePaymentResult) {
              paymentRequestRef.current = request
              setPaymentOptions(
                Object.entries(canMakePaymentResult)
                  .map(([key, value]) => value && key)
                  .filter(Boolean)
              )
            }
            setIsPaymentLoading(false)
          })
          .catch(() => {
            setIsPaymentLoading(false)
          })
      } catch (error) {
        Toast.error(error.message)
        setIsPaymentLoading(false)
      }
    }
  }, [stripe, amount, currency, paymentRequest])

  /**
   * Handle amount update of payment request
   * @see https://stripe.com/docs/js/payment_request/update
   * */
  useEffect(() => {
    if (paymentRequest && !paymentRequest.isShowing()) {
      paymentRequest.update({
        total: {
          amount: multiply(amount, 100),
          label: 'Total',
        },
      })
    }
  }, [paymentRequest, amount])

  /**
   * Handle paymentRequest interface initialization (click on pay)
   * @see https://stripe.com/docs/js/payment_request/events/on_paymentmethod
   * */
  useEffect(() => {
    if (paymentRequest) {
      paymentRequest.on(
        'paymentmethod',
        async ({ paymentMethod, complete, walletName }) => {
          setIsPaymentLoading(true)

          await onPaymentSuccess({
            onStripePaymentConfirm: complete,
            paymentMethodId: paymentMethod.id,
            paymentMethod: walletName,
          })

          setIsPaymentLoading(false)
        }
      )
    }
    return () => {
      paymentRequest?.off('paymentmethod')
    }
  }, [stripe, executeSetupPaymentMutation, paymentRequest, onPaymentSuccess])

  return [
    executePayment,
    {
      isLoading: isSetupPaymentLoading || isPaymentLoading,
      availablePaymentsOptions: paymentOptions,
    },
  ] as const
}
